/**
 * useCaseCreation.js
 * ==================
 * Central state management for the case creation wizard.
 *
 * Dette composable håndterer al data og logik for sagoprettelse.
 * Det fungerer som en "single source of truth" for alle steps i wizarden,
 * så data kan deles mellem komponenter uden prop-drilling.
 *
 * NY WIZARD STRUKTUR (3-4 steps):
 * - Step 1: Grundlag - Upload alle dokumenter, AI kategoriserer
 * - Step 2: Skyldner - AI pre-udfylder, bruger verificerer
 * - Step 3: Ekstra information - KUN hvis påkrævede custom variables (skjult ellers)
 * - Step 4: Bekræftelse - Samlet overblik før afsendelse
 *
 * AI Funktioner:
 * - Kategoriserer dokumenter automatisk (Grundlag, Kreditnotaer, Rykkere, Øvrig)
 * - Bestemmer sagstype baseret på dokumenter
 * - Udtrækker skyldner-info og matcher med eksisterende
 *
 * Brug:
 * import { useCaseCreation } from './composables/useCaseCreation'
 * const { currentStep, caseData, nextStep, prevStep } = useCaseCreation()
 */

import { reactive, computed } from '@vue/composition-api'

// ============================================================
// CONSTANTS - Definerer wizard-strukturen
// ============================================================

/**
 * Ny 3-4 steps wizard struktur.
 * Step 3 (customVariables) vises kun hvis der er påkrævede felter.
 */
const STEPS = [
  {
    id: 1, key: 'grundlag', name: 'Grundlag', optional: false,
  },
  {
    id: 2, key: 'skyldner', name: 'Skyldner', optional: false,
  },
  {
    id: 3, key: 'customVariables', name: 'Ekstra information', optional: true,
  },
  {
    id: 4, key: 'confirmation', name: 'Bekræftelse', optional: false,
  },
]

/**
 * Dokument kategorier for AI-kategorisering.
 * Bruges til at organisere uploadede dokumenter.
 */
const DOCUMENT_CATEGORIES = {
  GRUNDLAG: 'grundlag', // Fakturaer, domme, gældsbreve etc.
  KREDITNOTER: 'kreditnoter', // Kreditnotaer
  RYKKERE: 'rykkere', // Rykkerskrivelser
  OEVRIG: 'oevrig', // Øvrig dokumentation
  // UNCATEGORIZED removed - documents now start in OEVRIG
}

/**
 * Sagstyper der kan vælges i step 1.
 * Værdier matcher API'ets forventede base_type værdier.
 */
const CASE_TYPES = [
  {
    id: 1,
    name: 'Invoice',
    nameDa: 'Faktura',
    icon: 'FileTextIcon',
    description: 'Standard faktura til inddrivelse',
  },
  {
    id: 2,
    name: 'Court Verdict',
    nameDa: 'Dom',
    icon: 'AwardIcon',
    description: 'Retsafgørelse eller dom',
  },
  {
    id: 3,
    name: 'Promissory Note',
    nameDa: 'Gældsbrev',
    icon: 'FileIcon',
    description: 'Underskrevet gældsbrev',
  },
  {
    id: 4,
    name: 'Other',
    nameDa: 'Andet',
    icon: 'FolderIcon',
    description: 'Anden type grundlag',
  },
]

/**
 * Debitor-typer.
 * Vigtigt: 'sole-proprietorship' og 'capital-company' kræver CVR-nummer
 */
const DEBTOR_TYPES = [
  {
    value: 'person', label: 'Person', labelDa: 'Privatperson', requiresCvr: false,
  },
  {
    value: 'sole-proprietorship', label: 'Sole Proprietorship', labelDa: 'Enkeltmandsvirksomhed', requiresCvr: true,
  },
  {
    value: 'capital-company', label: 'Capital Company', labelDa: 'Selskab (ApS/A/S)', requiresCvr: true,
  },
]

// ============================================================
// STATE - Reaktiv data der deles på tværs af komponenter
// ============================================================

/**
 * Hoved-state objekt for hele wizarden.
 * NY STRUKTUR: Al dokumenthåndtering sker i Step 1 (Grundlag)
 * med AI-kategorisering.
 */
const state = reactive({
  // Navigation
  currentStep: 1,
  isLoading: false,
  error: null,

  // API reference - sættes når sagen oprettes på serveren
  debtCaseId: null,

  // AI Mode: 'test' = manuelt trigger, 'live' = automatisk
  aiMode: 'live',

  // AI-bestemt sagstype (1=Faktura, 2=Dom, 3=Gældsbrev, 4=Andet)
  caseType: {
    selectedType: null,
    aiDetermined: false, // True hvis AI bestemte typen
    aiConfidence: null, // 0-100 confidence score
  },

  // Step 1: Grundlag - ALLE dokumenter samlet
  // Dokumenter starter i oevrig og flyttes til kategorier
  documents: {
    grundlag: [], // Fakturaer, domme, gældsbreve - mindst 1 påkrævet
    kreditnoter: [], // Kreditnotaer
    rykkere: [], // Rykkerskrivelser
    oevrig: [], // Andre dokumenter (startpunkt for nye uploads)
  },

  // AI-analyse status
  aiAnalysis: {
    isAnalyzing: false,
    progress: 0, // 0-100
    currentDocument: null, // Dokument der analyseres
    completedCount: 0,
    totalCount: 0,
    errors: [], // Fejl under analyse
    currentFileProgress: 0, // Progress for nuværende fil (0-100%)
    scanningFiles: [], // Liste over filer der scannes med status
  },

  // Step 2: Skyldner(e) - med AI pre-fill
  debtors: [], // Array af skyldnere - mindst 1 påkrævet
  aiExtractedDebtor: null, // AI-udtrukket debtor-info fra dokumenter
  matchedExistingDebtor: null, // Match fra eksisterende debitorer

  // Step 3: Custom variables (fra API) - vises kun hvis påkrævede felter
  customVariables: {
    definitions: [], // Variable-definitioner fra server
    values: [], // Brugerens indtastede værdier
  },

  // Step 4: Bekræftelse
  confirmationAccepted: false,

  // Auto-save tracking
  lastSaved: null,
  hasUnsavedChanges: false,
})

// ============================================================
// FACTORY FUNCTIONS - Opretter tomme objekter med korrekt struktur
// ============================================================

/**
 * Opretter et generisk dokument objekt.
 * Bruges til ALLE dokumenttyper i den nye wizard.
 *
 * @param {string} category - Kategori dokumentet tilhører (default: 'oevrig')
 */
function createDocument(category = 'oevrig') {
  return {
    id: Date.now() + Math.random(), // Unik ID til Vue's :key
    file: null,
    category, // 'grundlag', 'kreditnoter', 'rykkere', 'oevrig'

    // Fælles felter
    title: '',
    isValid: false,

    // AI-analyse info
    aiAnalyzed: false,
    aiConfidence: null, // 0-100, sikkerhed på KATEGORISERINGEN
    aiDataConfidence: null, // 0-100, sikkerhed på de udtrukne beløb og datoer
    aiDebtorConfidence: null, // 0-100, sikkerhed på at rette part er valgt
    aiCategorySuggestion: null, // Foreslået kategori
    aiExtractedData: null, // Rå data fra AI
    aiWarnings: [], // Modellens egne forbehold
    aiPages: null, // { total, analyzed } - afslører delvist læste PDF'er

    // Grundlag-specifikke felter (fakturaer, domme, gældsbreve)
    sentAt: '', // Fakturadato / dokumentdato
    dueDate: '', // Betalingsfrist
    paymentAmount: '', // Beløb inkl. moms (påkrævet for grundlag)
    amountPaid: '', // Allerede betalt
    interestRate: '', // Rentesats

    // Kreditnota-specifikke felter
    creditNoteAmount: '',

    // Rykker-specifikke felter
    documentDate: '',
    reminderFee: 0, // Maks 100 kr per rykker
    hasCompensationFee: false, // Toggle - 310 kr hvis true, kun på ÉN rykker per grundlag
    interestAmount: 0, // Renter for denne rykker (ikke akkumulerede)

    // Øvrig dokumentation-specifikke felter
    categoryId: null, // Fra file-categories-dropdown API
  }
}

/**
 * LEGACY: Opretter et tomt base-dokument (faktura) objekt.
 * Bevaret for bagudkompatibilitet.
 */
function createEmptyBaseDocument() {
  const doc = createDocument('grundlag')
  return doc
}

/**
 * LEGACY: Opretter en tom kreditnota.
 * Bevaret for bagudkompatibilitet.
 */
function createEmptyCreditNote() {
  return createDocument('kreditnoter')
}

/**
 * LEGACY: Opretter en tom rykkerskrivelse.
 * Bevaret for bagudkompatibilitet.
 */
function createEmptyDebtReminder() {
  return createDocument('rykkere')
}

/**
 * LEGACY: Opretter et tomt "øvrig fil" objekt.
 * Bevaret for bagudkompatibilitet.
 */
function createEmptyOtherFile() {
  return createDocument('oevrig')
}

/**
 * Opretter en tom skyldner med alle påkrævede felter.
 * Default land er Danmark (ID: 247).
 */
function createEmptyDebtor() {
  return {
    id: Date.now(),
    debtorId: null, // ID fra server hvis eksisterende debitor
    isExisting: false, // True hvis valgt fra dropdown

    // Grundinfo
    name: '',
    co: '', // c/o adresse
    debtorType: '', // 'person', 'sole-proprietorship', 'capital-company'
    cpr: '', // Kun for personer
    cvr: '', // Kun for virksomheder

    // Bank info (valgfrit)
    bankBranchNo: '',
    bankAccountNo: '',
    cpn: '',

    // Adresse (påkrævet)
    address: {
      roadName: '',
      houseNumber: '',
      place: '', // Sted/Område (valgfrit)
      zipCode: '',
      city: '',
      countryId: 247, // Danmark som default
    },

    // Kontakt (valgfrit)
    phone: '',
    email: '',

    isValid: false,
  }
}

// ============================================================
// COMPUTED PROPERTIES - Afledte værdier
// ============================================================

/**
 * Returnerer info om det aktuelle step.
 */
const currentStepInfo = computed(() => STEPS.find(s => s.id === state.currentStep) || STEPS[0])

/**
 * Tjekker om der er PÅKRÆVEDE custom variables.
 * Step 3 vises KUN hvis der er påkrævede felter.
 * Defineret først da den bruges af totalSteps.
 */
const hasRequiredCustomVariables = computed(() => state.customVariables.definitions.some(v => v.is_required))

/**
 * Beregner antal steps (ekskl. valgfrie hvis ikke relevant)
 */
const totalSteps = computed(() => {
  if (hasRequiredCustomVariables.value) {
    return STEPS.length
  }
  return STEPS.length - 1 // Eksklud step 3 hvis ingen påkrævede custom vars
})

/**
 * Beregner samlet fremdrift i procent.
 */
const progressPercentage = computed(() => Math.round((state.currentStep / totalSteps.value) * 100))

/**
 * Alle dokumenter på tværs af kategorier.
 */
const allDocuments = computed(() => [
  ...state.documents.grundlag,
  ...state.documents.kreditnoter,
  ...state.documents.rykkere,
  ...state.documents.oevrig,
])

/**
 * Antal dokumenter i alle kategorier.
 */
const categorizedDocumentsCount = computed(() => state.documents.grundlag.length
  + state.documents.kreditnoter.length
  + state.documents.rykkere.length
  + state.documents.oevrig.length)

/**
 * Tjekker om der er nogen dokumenter uploadet overhovedet.
 * Bruges til at vise/skjule navigation.
 */
const hasAnyDocuments = computed(() => state.documents.grundlag.length > 0
  || state.documents.kreditnoter.length > 0
  || state.documents.rykkere.length > 0
  || state.documents.oevrig.length > 0)

/**
 * Returnerer valideringsfejl for Step 1 (Grundlag).
 * Bruges til at vise brugeren hvad der mangler.
 */
const step1ValidationErrors = computed(() => {
  const errors = []

  // Check: Mindst ét grundlag dokument
  if (state.documents.grundlag.length === 0) {
    errors.push('Flyt mindst ét grundlag-dokument (faktura) til kategorien "Grundlag / Fakturaer"')
    return errors
  }

  // Check: Alle grundlag dokumenter skal være gyldige
  const invalidDocs = state.documents.grundlag.filter(doc => !doc.isValid)
  invalidDocs.forEach(doc => {
    const missing = []
    if (!doc.sentAt) missing.push('fakturadato')
    if (!doc.dueDate) missing.push('forfaldsdato')
    if (!doc.paymentAmount) missing.push('beløb')

    if (missing.length > 0) {
      const docName = doc.title || (doc.file && doc.file.name) || 'Dokument'
      errors.push(`"${docName}" mangler: ${missing.join(', ')}`)
    }
  })

  return errors
})

/**
 * Returnerer valideringsfejl for Step 2 (Skyldner).
 * Bruges til at vise brugeren hvad der mangler.
 */
const step2ValidationErrors = computed(() => {
  const errors = []

  if (state.debtors.length === 0) {
    errors.push('Tilføj mindst én skyldner')
    return errors
  }

  state.debtors.forEach((debtor, index) => {
    if (debtor.isValid) return

    const missing = []
    const label = debtor.name || `Skyldner ${index + 1}`

    if (!debtor.debtorType) missing.push('skyldnertype')
    if (!debtor.name || debtor.name.trim() === '') missing.push('navn')
    if (debtor.debtorType && debtor.debtorType !== 'person' && (!debtor.cvr || debtor.cvr.length !== 8)) {
      missing.push('CVR-nummer (8 cifre)')
    }
    if (!debtor.address.roadName || debtor.address.roadName.trim() === '') missing.push('vejnavn')
    if (!debtor.address.houseNumber || debtor.address.houseNumber.trim() === '') missing.push('husnummer')
    if (!debtor.address.zipCode || debtor.address.zipCode.trim() === '') missing.push('postnummer')
    if (!debtor.address.city || debtor.address.city.trim() === '') missing.push('by')

    if (missing.length > 0) {
      errors.push(`"${label}" mangler: ${missing.join(', ')}`)
    }
  })

  return errors
})

/**
 * Sætter confirmation-accepted status for Step 4.
 */
function setConfirmationAccepted(value) {
  state.confirmationAccepted = value
}

/**
 * Tjekker om det aktuelle step er udfyldt korrekt.
 * NY LOGIK for 3-4 steps wizard.
 */
const isCurrentStepValid = computed(() => {
  switch (state.currentStep) {
    case 1:
      // Step 1 (Grundlag): Mindst ét kategoriseret grundlag-dokument med gyldige felter
      // Mindst ét grundlag-dokument med gyldige felter
      return state.documents.grundlag.length > 0
             && state.documents.grundlag.every(doc => doc.isValid)

    case 2:
      // Step 2 (Skyldner): Mindst én skyldner med alle påkrævede felter
      return state.debtors.length > 0
             && state.debtors.every(d => d.isValid)

    case 3: {
      // Step 3 (Ekstra information): Alle påkrævede custom variables
      const requiredVars = state.customVariables.definitions.filter(v => v.is_required)
      return requiredVars.every(reqVar => {
        const value = state.customVariables.values.find(v => v.customVarLabel === reqVar.variable)
        return value && value.value !== ''
      })
    }

    case 4:
      // Step 4 (Bekræftelse): Kræver at brugeren har accepteret
      return state.confirmationAccepted

    default:
      return false
  }
})

/**
 * Tjekker om der er custom variables (inkl. valgfrie).
 * Bevaret for bagudkompatibilitet.
 */
const hasCustomVariables = computed(() => state.customVariables.definitions.length > 0)

/**
 * Beregner samlet rykkergebyr.
 * Må ikke overstige 300 kr.
 */
const totalReminderFee = computed(() => state.documents.rykkere.reduce((sum, r) => sum + (Number(r.reminderFee) || 0), 0))

/**
 * Beregner samlet kompensationsgebyr.
 * Er 310 kr per rykker med hasCompensationFee = true.
 * Må kun være på ÉN rykker per grundlag (310 kr total per grundlag).
 */
const totalCompensationFee = computed(() => state.documents.rykkere.filter(r => r.hasCompensationFee).length * 310)

/**
 * Beregner samlede renter fra alle rykkere.
 */
const totalInterestAmount = computed(() => state.documents.rykkere.reduce((sum, r) => sum + (Number(r.interestAmount) || 0), 0))

/**
 * Tjekker om kompensationsgebyr allerede er sat på en rykker.
 * Bruges til at disable toggle på andre rykkere.
 */
const hasCompensationFeeSet = computed(() => state.documents.rykkere.some(r => r.hasCompensationFee))

/**
 * Maksimalt tilladt kompensationsgebyr baseret på antal grundlag-dokumenter.
 */
const maxCompensationFee = computed(() => state.documents.grundlag.length * 310)

// ============================================================
// METHODS - Handlinger der ændrer state
// ============================================================

/**
 * Går til næste step hvis validering tillader det.
 * NY LOGIK: 3-4 steps (spring step 3 over hvis ingen påkrævede custom vars)
 */
function nextStep() {
  if (!isCurrentStepValid.value) return false

  // Spring step 3 over hvis der ikke er påkrævede custom variables
  if (state.currentStep === 2 && !hasRequiredCustomVariables.value) {
    state.currentStep = 4
  } else if (state.currentStep < STEPS.length) {
    state.currentStep += 1
  }

  state.hasUnsavedChanges = true
  return true
}

/**
 * Går til forrige step.
 * NY LOGIK: Spring step 3 over hvis ingen påkrævede custom vars
 */
function prevStep() {
  // Reset bekræftelse hvis vi forlader step 4
  if (state.currentStep === 4) {
    state.confirmationAccepted = false
  }

  // Spring step 3 over hvis der ikke er påkrævede custom variables
  if (state.currentStep === 4 && !hasRequiredCustomVariables.value) {
    state.currentStep = 2
  } else if (state.currentStep > 1) {
    state.currentStep -= 1
  }
}

/**
 * Går direkte til et specifikt step.
 * Bruges når man klikker på step-indikatoren.
 */
function goToStep(stepNumber) {
  // Tillad kun at gå til steps man har gennemført eller det nuværende
  if (stepNumber <= state.currentStep || stepNumber === state.currentStep + 1) {
    // Spring step 3 over hvis det ikke er relevant
    if (stepNumber === 3 && !hasRequiredCustomVariables.value) {
      return
    }
    // Reset bekræftelse hvis vi forlader step 4
    if (state.currentStep === 4 && stepNumber !== 4) {
      state.confirmationAccepted = false
    }
    state.currentStep = stepNumber
  }
}

// ============================================================
// DOCUMENT MANAGEMENT - Ny struktur med kategorier
// ============================================================

/**
 * Tilføjer et dokument til en kategori.
 * @param {File} file - Fil der skal tilføjes
 * @param {string} category - Kategori (default: 'oevrig')
 */
function addDocument(file, category = null) {
  const targetCategory = category || 'oevrig'
  const doc = createDocument(targetCategory)
  doc.file = file
  doc.title = file.name.replace(/\.[^/.]+$/, '') // Filnavn uden extension

  if (state.documents[targetCategory]) {
    state.documents[targetCategory].push(doc)
  } else {
    state.documents.oevrig.push(doc)
  }

  state.hasUnsavedChanges = true
  return doc
}

/**
 * Flytter et dokument fra én kategori til en anden.
 * Bruges til drag-and-drop mellem kategorier.
 *
 * @param {number} docId - Dokument ID
 * @param {string} fromCategory - Nuværende kategori
 * @param {string} toCategory - Ny kategori
 */
function moveDocument(docId, fromCategory, toCategory) {
  if (!state.documents[fromCategory] || !state.documents[toCategory]) {
    console.error('Invalid category:', fromCategory, 'or', toCategory)
    return false
  }

  const fromIndex = state.documents[fromCategory].findIndex(d => d.id === docId)
  if (fromIndex === -1) {
    console.error('Document not found in category:', fromCategory)
    return false
  }

  // Flyt dokumentet
  const [doc] = state.documents[fromCategory].splice(fromIndex, 1)
  doc.category = toCategory
  state.documents[toCategory].push(doc)

  state.hasUnsavedChanges = true
  return true
}

/**
 * Fjerner et dokument fra enhver kategori.
 *
 * @param {number} docId - Dokument ID
 */
function removeDocument(docId) {
  const categories = ['grundlag', 'kreditnoter', 'rykkere', 'oevrig']

  for (const category of categories) {
    const index = state.documents[category].findIndex(d => d.id === docId)
    if (index > -1) {
      state.documents[category].splice(index, 1)
      state.hasUnsavedChanges = true
      return true
    }
  }
  return false
}

/**
 * Finder et dokument på tværs af alle kategorier.
 * Defineret før updateDocument og setDocumentAiResult da de bruger denne funktion.
 *
 * @param {number} docId - Dokument ID
 * @returns {Object|null} Dokumentet eller null
 */
function findDocumentById(docId) {
  const categories = ['grundlag', 'kreditnoter', 'rykkere', 'oevrig']

  for (const category of categories) {
    const doc = state.documents[category].find(d => d.id === docId)
    if (doc) return doc
  }
  return null
}

/**
 * Validerer et dokument baseret på dets kategori.
 * Forskellige kategorier har forskellige påkrævede felter.
 * Defineret før setDocumentAiResult da den bruger denne funktion.
 *
 * @param {Object} doc - Dokument objekt
 * @param {string} category - Kategori at validere for
 * @returns {boolean} Om dokumentet er gyldigt
 */
function validateDocumentForCategory(doc, category) {
  switch (category) {
    case 'grundlag':
      // Grundlag kræver: dato, forfaldsdato og beløb
      return !!(doc.sentAt && doc.dueDate && doc.paymentAmount)

    case 'kreditnoter':
      // Kreditnotaer kræver: dato og beløb
      return !!(doc.sentAt && doc.creditNoteAmount)

    case 'rykkere':
      // Rykkere kræver: dokumentdato
      return !!(doc.documentDate || doc.sentAt)

    case 'oevrig':
      // Øvrig dokumentation: altid gyldig hvis der er en fil
      return !!doc.file

    default:
      // Uncategorized: aldrig gyldig (skal kategoriseres)
      return false
  }
}

/**
 * Opdaterer et felt på et dokument.
 *
 * @param {number} docId - Dokument ID
 * @param {string} field - Felt der skal opdateres
 * @param {any} value - Ny værdi
 */
function updateDocument(docId, field, value) {
  const doc = findDocumentById(docId)
  if (doc) {
    doc[field] = value
    state.hasUnsavedChanges = true
    return true
  }
  return false
}

/**
 * Sætter AI-analyse resultat på et dokument.
 * Inkluderer validering af rykkergebyr (max 100 kr) og kompensationsgebyr (310 kr toggle).
 *
 * @param {number} docId - Dokument ID
 * @param {Object} aiResult - AI-analyse resultat
 */
function setDocumentAiResult(docId, aiResult) {
  const doc = findDocumentById(docId)
  if (!doc) return false

  doc.aiAnalyzed = true
  // 'confidence' gælder kategoriseringen. Feltværdiernes og debitorvalgets
  // sikkerhed er selvstændige tal — de blev tidligere slået sammen til ét,
  // så en sikker kategorisering fik felterne til at se sikre ud.
  doc.aiConfidence = aiResult.confidence ?? null
  doc.aiDataConfidence = aiResult.dataConfidence ?? null
  doc.aiDebtorConfidence = aiResult.debtorConfidence ?? null
  doc.aiCategorySuggestion = aiResult.category || null
  doc.aiExtractedData = aiResult.data || null
  doc.aiWarnings = aiResult.warnings || []
  doc.aiPages = aiResult.pages || null

  // Auto-udfyld felter fra AI
  if (aiResult.data) {
    if (aiResult.data.title) doc.title = aiResult.data.title
    if (aiResult.data.invoiceDate) doc.sentAt = aiResult.data.invoiceDate
    if (aiResult.data.dueDate) {
      doc.dueDate = aiResult.data.dueDate
    } else if (aiResult.data.invoiceDate && aiResult.category === 'grundlag') {
      // Fallback: Sæt forfaldsdato til fakturadato + 7 dage hvis AI ikke finder forfaldsdato
      const invoiceDate = new Date(aiResult.data.invoiceDate)
      invoiceDate.setDate(invoiceDate.getDate() + 7)
      const [dateOnly] = invoiceDate.toISOString().split('T')
      doc.dueDate = dateOnly
    }
    if (aiResult.data.totalAmount) doc.paymentAmount = aiResult.data.totalAmount.toString()
    if (aiResult.data.interestRate) doc.interestRate = aiResult.data.interestRate.toString()
    if (aiResult.data.creditNoteAmount) doc.creditNoteAmount = aiResult.data.creditNoteAmount.toString()
    if (aiResult.data.documentDate) doc.documentDate = aiResult.data.documentDate

    // Rykker-specifikke felter med validering
    if (aiResult.category === 'rykkere') {
      // Rykkergebyr: Cap til max 100 kr
      if (aiResult.data.reminderFee !== undefined && aiResult.data.reminderFee !== null) {
        const fee = Number(aiResult.data.reminderFee)
        doc.reminderFee = Math.min(fee, 100) // Max 100 kr per rykker
        if (fee > 100) {
          doc.aiWarnings.push(`Rykkergebyr reduceret fra ${fee} kr til 100 kr (lovgivningsmæssig grænse)`)
        }
      }

      // Kompensationsgebyr: Toggle baseret på om 310 kr fremgår
      // Kun sæt til true hvis der ikke allerede er en rykker med kompensationsgebyr
      if (aiResult.data.hasCompensationFee === true) {
        const alreadyHasCompensation = state.documents.rykkere.some(r => r.id !== docId && r.hasCompensationFee)
        if (!alreadyHasCompensation) {
          doc.hasCompensationFee = true
        } else {
          doc.aiWarnings.push('Kompensationsgebyr (310 kr) er allerede sat på en anden rykker - kan kun opkræves én gang per grundlag')
        }
      }

      // Renter: Sæt direkte (AI er instrueret i at undgå dobbeltføring)
      if (aiResult.data.interestAmount !== undefined && aiResult.data.interestAmount !== null) {
        doc.interestAmount = Number(aiResult.data.interestAmount)
      }

      // Fallback for forfaldsdato på rykkere: dokumentdato + 10 dage
      if (!doc.dueDate && doc.documentDate) {
        const docDate = new Date(doc.documentDate)
        docDate.setDate(docDate.getDate() + 10)
        const [dateOnly] = docDate.toISOString().split('T')
        doc.dueDate = dateOnly
      }
    }
  }

  // Validér dokument baseret på kategori (eller forventet kategori fra AI)
  const targetCategory = aiResult.category || doc.category
  doc.isValid = validateDocumentForCategory(doc, targetCategory)

  state.hasUnsavedChanges = true
  return true
}

/**
 * Tilføjer et nyt base-dokument (faktura).
 */
function addBaseDocument() {
  state.documents.baseDocuments.push(createEmptyBaseDocument())
  state.hasUnsavedChanges = true
}

/**
 * Tilføjer et nyt base-dokument (faktura) med en fil allerede vedhæftet.
 * Bruges når brugeren drag-and-dropper eller vælger filer direkte.
 */
function addBaseDocumentWithFile(file) {
  const doc = createEmptyBaseDocument()
  doc.file = file
  state.documents.baseDocuments.push(doc)
  state.hasUnsavedChanges = true
  return doc
}

/**
 * Fjerner et base-dokument.
 */
function removeBaseDocument(id) {
  const index = state.documents.baseDocuments.findIndex(d => d.id === id)
  if (index > -1) {
    state.documents.baseDocuments.splice(index, 1)
    state.hasUnsavedChanges = true
  }
}

/**
 * Tilføjer en ny kreditnota.
 */
function addCreditNote() {
  state.documents.creditNotes.push(createEmptyCreditNote())
  state.hasUnsavedChanges = true
}

/**
 * Tilføjer en ny kreditnota med en fil allerede vedhæftet.
 * Bruges når brugeren drag-and-dropper eller vælger filer direkte.
 */
function addCreditNoteWithFile(file) {
  const note = createEmptyCreditNote()
  note.file = file
  state.documents.creditNotes.push(note)
  state.hasUnsavedChanges = true
  return note
}

/**
 * Fjerner en kreditnota.
 */
function removeCreditNote(id) {
  const index = state.documents.creditNotes.findIndex(n => n.id === id)
  if (index > -1) {
    state.documents.creditNotes.splice(index, 1)
    state.hasUnsavedChanges = true
  }
}

/**
 * Tilføjer en ny rykkerskrivelse.
 */
function addDebtReminder() {
  state.reminders.debtReminders.push(createEmptyDebtReminder())
  state.hasUnsavedChanges = true
}

/**
 * Tilføjer en ny rykkerskrivelse med en fil allerede vedhæftet.
 * Bruges når brugeren drag-and-dropper eller vælger filer direkte.
 */
function addDebtReminderWithFile(file) {
  const reminder = createEmptyDebtReminder()
  reminder.file = file
  state.reminders.debtReminders.push(reminder)
  state.hasUnsavedChanges = true
  return reminder
}

/**
 * Fjerner en rykkerskrivelse.
 */
function removeDebtReminder(id) {
  const index = state.reminders.debtReminders.findIndex(r => r.id === id)
  if (index > -1) {
    state.reminders.debtReminders.splice(index, 1)
    state.hasUnsavedChanges = true
  }
}

/**
 * Tilføjer et nyt "øvrigt dokument".
 */
function addOtherFile() {
  state.reminders.otherFiles.push(createEmptyOtherFile())
  state.hasUnsavedChanges = true
}

/**
 * Tilføjer et nyt "øvrigt dokument" med en fil allerede vedhæftet.
 * Bruges når brugeren drag-and-dropper eller vælger filer direkte.
 */
function addOtherFileWithFile(file) {
  const otherFile = createEmptyOtherFile()
  otherFile.file = file
  state.reminders.otherFiles.push(otherFile)
  state.hasUnsavedChanges = true
  return otherFile
}

/**
 * Fjerner et "øvrigt dokument".
 */
function removeOtherFile(id) {
  const index = state.reminders.otherFiles.findIndex(f => f.id === id)
  if (index > -1) {
    state.reminders.otherFiles.splice(index, 1)
    state.hasUnsavedChanges = true
  }
}

/**
 * Tilføjer en ny skyldner.
 */
function addDebtor() {
  state.debtors.push(createEmptyDebtor())
  state.hasUnsavedChanges = true
}

/**
 * Fjerner en skyldner.
 */
function removeDebtor(id) {
  // Tillad kun fjernelse hvis der er mere end én skyldner
  if (state.debtors.length > 1) {
    const index = state.debtors.findIndex(d => d.id === id)
    if (index > -1) {
      state.debtors.splice(index, 1)
      state.hasUnsavedChanges = true
    }
  }
}

/**
 * Nulstiller hele wizarden til start-tilstand.
 * Bruges efter succesfuld oprettelse eller hvis brugeren vil starte forfra.
 */
function resetWizard() {
  state.currentStep = 1
  state.debtCaseId = null
  state.error = null

  // AI mode og sagstype
  state.aiMode = 'test'
  state.caseType.selectedType = null
  state.caseType.aiDetermined = false
  state.caseType.aiConfidence = null

  // Dokumenter
  state.documents.grundlag = []
  state.documents.kreditnoter = []
  state.documents.rykkere = []
  state.documents.oevrig = []

  // AI analyse
  state.aiAnalysis.isAnalyzing = false
  state.aiAnalysis.progress = 0
  state.aiAnalysis.currentDocument = null
  state.aiAnalysis.completedCount = 0
  state.aiAnalysis.totalCount = 0
  state.aiAnalysis.errors = []
  state.aiAnalysis.currentFileProgress = 0
  state.aiAnalysis.scanningFiles = []

  // Skyldnere
  state.debtors = []
  state.aiExtractedDebtor = null
  state.matchedExistingDebtor = null

  // Custom variables
  state.customVariables.values = []

  // Bekræftelse
  state.confirmationAccepted = false

  state.hasUnsavedChanges = false
}

/**
 * Initialiserer wizarden.
 * NY LOGIK: Ingen pre-oprettede dokumenter - brugeren uploader først.
 */
function initializeWizard() {
  // Start med én tom skyldner (vil blive AI-udfyldt)
  if (state.debtors.length === 0) {
    addDebtor()
  }
}

/**
 * Sætter AI mode (test eller live).
 * @param {string} mode - 'test' eller 'live'
 */
function setAiMode(mode) {
  if (mode === 'test' || mode === 'live') {
    state.aiMode = mode
  }
}

/**
 * Sætter AI-udtrukket debtor info.
 * Bruges af AI-servicen til at gemme udtrukket skyldner-data.
 */
function setAiExtractedDebtor(debtorData) {
  state.aiExtractedDebtor = debtorData
  state.hasUnsavedChanges = true
}

/**
 * Sætter matched eksisterende debtor.
 * Bruges når AI finder en match i eksisterende skyldnere.
 */
function setMatchedExistingDebtor(debtorId, matchData) {
  state.matchedExistingDebtor = { id: debtorId, ...matchData }
  state.hasUnsavedChanges = true
}

/**
 * Sætter AI-analyse status.
 */
function setAiAnalysisStatus(status) {
  Object.assign(state.aiAnalysis, status)
}

// ============================================================
// EXPORTS
// ============================================================

/**
 * Hook til at bruge case creation state i komponenter.
 * Returnerer alle nødvendige data og metoder.
 *
 * UDVIDET med nye AI-kategorisering og dokument-håndtering funktioner.
 */
export function useCaseCreation() {
  return {
    // State
    state,

    // Constants
    STEPS,
    CASE_TYPES,
    DEBTOR_TYPES,
    DOCUMENT_CATEGORIES,

    // Computed
    currentStepInfo,
    progressPercentage,
    totalSteps,
    isCurrentStepValid,
    hasCustomVariables,
    hasRequiredCustomVariables,
    allDocuments,
    categorizedDocumentsCount,
    hasAnyDocuments,
    step1ValidationErrors,
    step2ValidationErrors,
    totalReminderFee,
    totalCompensationFee,
    totalInterestAmount,
    hasCompensationFeeSet,
    maxCompensationFee,

    // Navigation
    nextStep,
    prevStep,
    goToStep,

    // NY: Dokument-håndtering med kategorier
    addDocument,
    moveDocument,
    removeDocument,
    updateDocument,
    findDocumentById,
    setDocumentAiResult,
    validateDocumentForCategory,

    // LEGACY: Document methods (for backwards compatibility)
    addBaseDocument,
    addBaseDocumentWithFile,
    removeBaseDocument,
    addCreditNote,
    addCreditNoteWithFile,
    removeCreditNote,

    // LEGACY: Reminder methods (for backwards compatibility)
    addDebtReminder,
    addDebtReminderWithFile,
    removeDebtReminder,
    addOtherFile,
    addOtherFileWithFile,
    removeOtherFile,

    // Debtor methods
    addDebtor,
    removeDebtor,

    // NY: AI mode og debtor matching
    setAiMode,
    setAiExtractedDebtor,
    setMatchedExistingDebtor,
    setAiAnalysisStatus,
    setConfirmationAccepted,

    // Factory functions (for creating empty objects)
    createDocument,
    createEmptyBaseDocument,
    createEmptyCreditNote,
    createEmptyDebtReminder,
    createEmptyOtherFile,
    createEmptyDebtor,

    // Wizard control
    initializeWizard,
    resetWizard,
  }
}

// Default export for backwards compatibility
export default useCaseCreation
